import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import UpdateProfileUI from "../../Pages/profile/updateProfile";
import { fetchCountries, updateUser, uploadMediaFile } from "../../services/api";
import { Profile } from "../../types/types";
import { FormData } from "../../types/updateProfileProps";

type UpdatePanelProps = {
    member: Profile;
    onClose: () => void;
    onSubmitSuccess: () => void;
};

export const UpdateProfileFeature: React.FC<UpdatePanelProps> = ({
    member,
    onClose,
    onSubmitSuccess,
}) => {
    const [profileImage, setProfileImage] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [countries, setCountries] = useState<string[]>([]);
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
    const { t } = useTranslation("updateProfile");

    const initialFormData: FormData = {
        firstName: member?.firstName || "",
        lastName: member?.lastName || "",
        phone: member?.phone || "",
        nationality: member?.nationality || "",
        residenceCountry: member?.residenceCountry || "",
        organizationName: member?.manager?.organizationName || "",
        bio: member?.bio || "",
    };

    const [formData, setFormData] = useState<FormData>(initialFormData);

    useEffect(() => {
        const loadCountries = async () => {
            try {
                const fetchedCountries = await fetchCountries();
                setCountries(fetchedCountries);
            } catch (err: any) {
                toast.error(
                    err?.response?.data?.message ||
                    err?.response?.data?.errors?.[0] ||
                    t("validation.countryLoadFailed")
                );
            }
        };

        loadCountries();
    }, [t]);

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
            e.target.value = "";
            setValidationErrors((prev) => ({
                ...prev,
                profileImage: t("validation.imageFormat"),
            }));
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            e.target.value = "";
            setValidationErrors((prev) => ({
                ...prev,
                profileImage: t("validation.imageSize"),
            }));
            return;
        }

        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }

        setProfileImage(file);
        setPreviewUrl(URL.createObjectURL(file));
        setValidationErrors((prev) => ({ ...prev, profileImage: "" }));
    };

    const validateForm = () => {
        const errors: { [key: string]: string } = {};
        const firstName = formData.firstName.trim();
        const lastName = formData.lastName.trim();
        const nationality = formData.nationality.trim();
        const residenceCountry = formData.residenceCountry.trim();
        const bio = formData.bio.trim();

        if (!firstName) {
            errors.firstName = t("validation.firstNameRequired");
        }

        if (!lastName) {
            errors.lastName = t("validation.lastNameRequired");
        }

        if (!nationality) {
            errors.nationality = t("validation.nationalityRequired");
        }

        if (!residenceCountry) {
            errors.residenceCountry = t("validation.residenceCountryRequired");
        }

        if (bio.length > 1000) {
            errors.bio = t("validation.bioLength");
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const updateProfileInLocalStorage = (updateData: any) => {
        const saved = localStorage.getItem("loginResponse");
        if (!saved) return;

        try {
            const parsed = JSON.parse(saved);

            if (updateData.profileImageUrl) {
                parsed.profilePicture = updateData.profileImageUrl;
            }

            if (updateData.firstName) {
                parsed.firstName = updateData.firstName;
            }

            if (updateData.lastName) {
                parsed.lastName = updateData.lastName;
            }

            if (updateData.bio) {
                parsed.bio = updateData.bio;
            }

            if (updateData.residenceCountry) {
                parsed.country = updateData.residenceCountry;
            }

            if (updateData.managerDto?.organizationName) {
                parsed.organizationName = updateData.managerDto.organizationName;
            }

            localStorage.setItem("loginResponse", JSON.stringify(parsed));
        } catch (err) {
            console.error("Failed to update profile in localStorage:", err);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setError(null);
        setIsSubmitting(true);

        try {
            const uploadedImageUrl = profileImage ? await uploadMediaFile(profileImage) : undefined;
            const updateUserDto: any = {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                phone: formData.phone.trim(),
                nationality: formData.nationality.trim(),
                residenceCountry: formData.residenceCountry.trim(),
                bio: formData.bio.trim(),
            };

            if (member.profileType === "ORGANIZATION") {
                updateUserDto.managerDto = {
                    organizationName: formData.organizationName.trim(),
                };
            }

            if (uploadedImageUrl) {
                updateUserDto.profileImageUrl = uploadedImageUrl;
            }

            await updateUser(updateUserDto);
            toast.success(t("profile_updated_successfully"));
            updateProfileInLocalStorage(updateUserDto);
            onSubmitSuccess();
        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.errors?.[0] ||
                t("validation.updateFailed");
            setError(message);
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <UpdateProfileUI
            previewUrl={previewUrl}
            error={error}
            isSubmitting={isSubmitting}
            countries={countries}
            validationErrors={validationErrors}
            formData={formData}
            member={member}
            handleInputChange={handleInputChange}
            handleFileChange={handleFileChange}
            handleSubmit={handleSubmit}
            onClose={onClose}
        />
    );
};
